/**
 * A defensive reader for a raw medium document.
 *
 * Every Firebase surface wraps this in its own converter later, so this is where an
 * untrusted document becomes a `Medium` or is refused. Two rules:
 *
 * - **Unknown fields are dropped**, never passed through. A stored doc from a newer schema
 *   must not smuggle fields into a running client.
 * - **A doc that cannot be a medium throws**, rather than being silently patched into one.
 *   A missing slug or an unknown family is a broken record, not a defaultable one.
 */
/** A raw document that cannot be read as a medium. */
export class InvalidMediumError extends Error {
    problems;
    constructor(problems) {
        super(`cannot read medium document: ${problems.join('; ')}`);
        this.name = 'InvalidMediumError';
        this.problems = problems;
    }
}
const FAMILIES = [
    'book',
    'card',
    'theatre',
    'optical',
    'motion',
    'other',
    'digital',
];
const AVAILABILITIES = ['concept', 'prototype', 'available'];
const YOUNG_SAFE = ['ok', 'caution', 'no'];
const PRODUCTION_EASE = ['high', 'med', 'low'];
const CUTTER = ['none', 'helpful', 'required'];
const RISK = ['low', 'med', 'high'];
const REFILL = ['strong', 'possible', 'no'];
const NARRATIVE = ['high', 'med', 'low'];
const FRAME_MODELS = [
    'discrete-scenes',
    'silhouette',
    'panorama',
    'two-image',
    'motion-frames',
    'other',
];
const LAYOUTS = [
    'sequence',
    'depth',
    'stage',
    'panorama',
    'pair',
    'figure',
    'tokens',
    'frames',
    'film',
];
const INPUTS = ['flat', 'layers'];
const RENDERS = ['full-colour', 'silhouette'];
const SELECTS = ['all', 'first-n', 'hero', 'props', 'character'];
const PIECES = ['backdrop', 'character', 'prop', 'foreground'];
const OUTPUTS = [
    'page',
    'card',
    'plane',
    'strip',
    'disc',
    'figure',
    'token',
    'frame',
    'cut-file',
    'video',
];
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function readLocalized(value) {
    if (!isRecord(value))
        return undefined;
    const en = value['en'];
    if (typeof en !== 'string')
        return undefined;
    const tr = value['tr'];
    return typeof tr === 'string' ? { en, tr } : { en };
}
function readLocalizedList(value) {
    if (!Array.isArray(value))
        return undefined;
    const rows = value.map(readLocalized).filter((row) => row !== undefined);
    return rows.length > 0 ? rows : undefined;
}
/**
 * One photograph, or nothing.
 *
 * A malformed entry is **dropped**, never allowed to fail the record: a bad row in `photos`
 * is one missing picture on a store page, and refusing the whole medium over it would take a
 * keepsake off the shelf for a caption. `kind`, `path` and `caption` are the minimum a
 * surface can render — a clip additionally needs its `poster`, because a `<video>` with no
 * poster is a black rectangle until someone presses it.
 */
function readPhoto(value) {
    if (!isRecord(value))
        return undefined;
    const kind = value['kind'];
    if (kind !== 'still' && kind !== 'clip')
        return undefined;
    const path = readString(value['path']);
    const caption = readLocalized(value['caption']);
    if (path === undefined || caption === undefined)
        return undefined;
    const poster = readString(value['poster']);
    if (kind === 'clip' && poster === undefined)
        return undefined;
    const photo = { kind, path, caption };
    if (poster !== undefined)
        photo.poster = poster;
    const size = readString(value['size']);
    if (size !== undefined)
        photo.size = size;
    if (value['onHome'] === true)
        photo.onHome = true;
    return photo;
}
function readPhotos(value) {
    if (!Array.isArray(value))
        return undefined;
    const rows = value.map(readPhoto).filter((row) => row !== undefined);
    return rows.length > 0 ? rows : undefined;
}
function readEnum(value, allowed, fallback) {
    return typeof value === 'string' && allowed.includes(value)
        ? value
        : fallback;
}
function readEnumList(value, allowed) {
    if (!Array.isArray(value))
        return [];
    return value.filter((entry) => typeof entry === 'string' ? allowed.includes(entry) : false);
}
/**
 * The displayed price, or nothing.
 *
 * Refused rather than coerced when it is not a positive, finite number in one of the three
 * currencies — a record that arrives with a malformed price should render no price, not a
 * confident wrong one. `undefined` is what the surfaces treat as "unpublishable", which is the
 * honest reading of a broken value too.
 */
function readPrice(value) {
    if (typeof value !== 'object' || value === null)
        return undefined;
    const raw = value;
    const amount = raw['amount'];
    const currency = raw['currency'];
    if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0)
        return undefined;
    if (currency !== 'EUR' && currency !== 'TRY' && currency !== 'USD')
        return undefined;
    return { amount, currency };
}
function readNumber(value, fallback) {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
function readString(value) {
    return typeof value === 'string' && value.length > 0 ? value : undefined;
}
/** `0` is a real tier — "the base" (PRD 03 §4.7) — and not a missing one. */
function readTier(value) {
    return value === 0 || value === 1 || value === 2 || value === 3 ? value : null;
}
const PRINT_FORMS = [
    'codex',
    'leporello',
    'stack',
    'tunnel',
    'stage',
    'strip',
    'disc',
];
function readPositiveInteger(value) {
    return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined;
}
/**
 * The print block. A block with no readable `form` is dropped whole — geometry with no
 * archetype cannot drive a paper model — and a frame or unit that is not numbers is dropped
 * on its own, leaving the rest.
 */
function readPrint(value) {
    if (!isRecord(value))
        return undefined;
    const form = value['form'];
    if (typeof form !== 'string' || !PRINT_FORMS.includes(form)) {
        return undefined;
    }
    const print = { form: form };
    if (isRecord(value['frame'])) {
        const w = readPositiveInteger(value['frame']['w']);
        const h = readPositiveInteger(value['frame']['h']);
        if (w !== undefined && h !== undefined)
            print.frame = { w, h };
    }
    if (isRecord(value['unit'])) {
        const unit = value['unit'];
        const trim = isRecord(unit['trimMm']) ? unit['trimMm'] : {};
        const w = readNumber(trim['w'], 0);
        const h = readNumber(trim['h'], 0);
        if (w > 0 && h > 0) {
            print.unit = {
                trimMm: { w, h },
                bleedMm: Math.max(0, readNumber(unit['bleedMm'], 0)),
                safeMm: Math.max(0, readNumber(unit['safeMm'], 0)),
            };
            // The marker survives the read only as a literal `true`: a placeholder is a fact
            // about the numbers, and anything less definite is not one.
            if (unit['placeholder'] === true)
                print.unit.placeholder = true;
        }
    }
    if (isRecord(value['params'])) {
        const params = {};
        for (const [key, entry] of Object.entries(value['params'])) {
            if (typeof entry === 'number' || typeof entry === 'string' || typeof entry === 'boolean') {
                params[key] = entry;
            }
        }
        if (Object.keys(params).length > 0)
            print.params = params;
    }
    return print;
}
function readComposition(value) {
    if (!isRecord(value))
        return undefined;
    const layout = typeof value['layout'] === 'string' ? value['layout'] : '';
    if (!LAYOUTS.includes(layout))
        return undefined;
    const scenes = isRecord(value['scenes']) ? value['scenes'] : {};
    const min = readNumber(scenes['min'], 1);
    const max = readNumber(scenes['max'], min);
    const ideal = readNumber(scenes['ideal'], min);
    const composition = {
        layout: layout,
        input: readEnum(value['input'], INPUTS, 'flat'),
        render: readEnum(value['render'], RENDERS, 'full-colour'),
        scenes: { min, max, ideal },
        outputs: readEnumList(value['outputs'], OUTPUTS),
    };
    const select = readEnumList(value['select'] === undefined ? [] : [value['select']], SELECTS)[0];
    if (select !== undefined)
        composition.select = select;
    const pieces = readEnumList(value['pieces'], PIECES);
    if (pieces.length > 0)
        composition.pieces = pieces;
    return composition;
}
/**
 * Read an untrusted document as a `Medium`, filling defaults and dropping anything unknown.
 * Throws `InvalidMediumError` when the document cannot be a medium at all.
 */
export function normalizeMedium(raw) {
    const problems = [];
    if (!isRecord(raw)) {
        throw new InvalidMediumError(['document is not an object']);
    }
    const id = readString(raw['id']);
    if (id === undefined)
        problems.push('id is missing or empty');
    const name = readLocalized(raw['name']);
    if (name === undefined)
        problems.push('name is missing or has no English string');
    const physicalAction = readLocalized(raw['physicalAction']);
    if (physicalAction === undefined) {
        problems.push('physicalAction is missing or has no English string');
    }
    const family = raw['family'];
    if (typeof family !== 'string' || !FAMILIES.includes(family)) {
        problems.push(`family is missing or unknown (got ${JSON.stringify(family)})`);
    }
    if (problems.length > 0)
        throw new InvalidMediumError(problems);
    const medium = {
        id: id,
        name: name,
        family: family,
        physicalAction: physicalAction,
        published: raw['published'] === true,
        comingSoon: raw['comingSoon'] === true,
        availability: readEnum(raw['availability'], AVAILABILITIES, 'concept'),
        order: readNumber(raw['order'], 0),
        ageMin: readNumber(raw['ageMin'], 2),
        ageMax: readNumber(raw['ageMax'], 7),
        youngSafe: readEnum(raw['youngSafe'], YOUNG_SAFE, 'caution'),
        tier: readTier(raw['tier']),
        productionEase: readEnum(raw['productionEase'], PRODUCTION_EASE, 'med'),
        cutterDependency: readEnum(raw['cutterDependency'], CUTTER, 'none'),
        consistencyRisk: readEnum(raw['consistencyRisk'], RISK, 'med'),
        refillModel: readEnum(raw['refillModel'], REFILL, 'no'),
        narrativeStrength: readEnum(raw['narrativeStrength'], NARRATIVE, 'med'),
        frameModel: readEnum(raw['frameModel'], FRAME_MODELS, 'other'),
        createdAt: raw['createdAt'] ?? null,
        updatedAt: raw['updatedAt'] ?? null,
        createdBy: readString(raw['createdBy']) ?? 'unknown',
    };
    const optionalText = {
        formatLabel: readLocalized(raw['formatLabel']),
        tagline: readLocalized(raw['tagline']),
        description: readLocalized(raw['description']),
        storytellingQuality: readLocalized(raw['storytellingQuality']),
        ruledOutReason: readLocalized(raw['ruledOutReason']),
        generationNotes: readLocalized(raw['generationNotes']),
    };
    for (const [key, value] of Object.entries(optionalText)) {
        if (value !== undefined)
            medium[key] = value;
    }
    if (raw['featured'] === true)
        medium.featured = true;
    if (raw['ruledOut'] === true)
        medium.ruledOut = true;
    const illustrationPath = readString(raw['illustrationPath']);
    if (illustrationPath !== undefined)
        medium.illustrationPath = illustrationPath;
    const baseSku = readString(raw['baseSku']);
    if (baseSku !== undefined)
        medium.baseSku = baseSku;
    const refillSku = readString(raw['refillSku']);
    if (refillSku !== undefined)
        medium.refillSku = refillSku;
    const price = readPrice(raw['price']);
    if (price !== undefined)
        medium.price = price;
    const shipsIn = readString(raw['shipsIn']);
    if (shipsIn !== undefined)
        medium.shipsIn = shipsIn;
    const checkoutUrl = readString(raw['checkoutUrl']);
    if (checkoutUrl !== undefined)
        medium.checkoutUrl = checkoutUrl;
    const composition = readComposition(raw['composition']);
    if (composition !== undefined)
        medium.composition = composition;
    const print = readPrint(raw['print']);
    if (print !== undefined)
        medium.print = print;
    const whatArrives = readLocalizedList(raw['whatArrives']);
    if (whatArrives !== undefined)
        medium.whatArrives = whatArrives;
    const photos = readPhotos(raw['photos']);
    if (photos !== undefined)
        medium.photos = photos;
    return medium;
}
//# sourceMappingURL=normalize.js.map